import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HyperDxService } from './hyperdx.service';
import { IModule } from '../models/module.model';
import { ContextService } from './context.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // Propriedade para controlar o estado de autenticação
  private authenticated: boolean = false;
  private hyperDxService?: HyperDxService; // Injeção tardia para evitar dependência circular

  // BehaviorSubject para armazenar o estado do módulo selecionado
  private selectedModuleSubject: BehaviorSubject<IModule>;
  public selectedModule$: Observable<IModule>; // Observable público

  constructor(private contextService: ContextService) {
    // Verificar tokens no inicialização para definir o estado de autenticação
    this.authenticated = !!this.getToken();

    // Inicializar primeiro o BehaviorSubject para evitar acessos a undefined
    this.selectedModuleSubject = new BehaviorSubject<IModule>({ id: '', description: '' });
    this.selectedModule$ = this.selectedModuleSubject.asObservable();

    // Carregar do storage e aplicar após o Subject existir
    const storedModule = this.getSelectedModuleFromStorage();
    if (storedModule && storedModule.id) {
      this.setSelectedModule(storedModule);
    }
  }

  // Método para injetar o HyperDxService após a inicialização
  setHyperDxService(hyperDxService: HyperDxService): void {
    this.hyperDxService = hyperDxService;

    // Se o usuário já estiver logado, configura a identificação no HyperDX
    if (this.isLogged()) {
      this.hyperDxService.setUserIdentification();
    }
  }

  setUserId(id: string) {
    localStorage.setItem('userId', btoa(id.toString()));
  }

  setToken(token: String): void {
    localStorage.setItem('token', btoa(token.toString()));
  }

  setRefreshToken(refreshToken: string): void {
    localStorage.setItem('refreshToken', btoa(refreshToken.toString()));
  }

  getRefreshToken(): string {
    const stored = localStorage.getItem('refreshToken');
    return stored ? atob(stored) : '';
  }

  setUsername(username: String): void {
    localStorage.setItem('username', btoa(username.toString()));
  }

  setUserEmail(email: string): void {
    localStorage.setItem('userEmail', btoa(email.toString()));
  }

  setCompanyName(companyName: string): void {
    localStorage.setItem('companyName', btoa(companyName.toString()));
  }

  setUserImage(image: string): void {
    localStorage.setItem('userImage', btoa(image.toString()));
  }

  setPermission(permission: String): void {
    localStorage.setItem('permission', btoa(permission.toString()));
  }

  setAnamneseAuthType(authType: String): void {
    localStorage.setItem('anamneseAuthType', btoa(authType.toString()));
  }

  setSendDocsType(sendDocsType: String): void {
    localStorage.setItem('sendDocsType', btoa(sendDocsType.toString()));
  }

  setClinicCnpj(cnpj: string): void {
    localStorage.setItem('clinicCnpj', btoa(cnpj.toString()));
  }

  setIntercomJwt(jwt: string): void {
    localStorage.setItem('intercomJwt', btoa(jwt.toString()));
  }

  getIntercomJwt(): string {
    const stored = localStorage.getItem('intercomJwt');
    return stored ? atob(stored) : '';
  }

  setTreatmentPlanPermission(hasPermission: boolean): void {
    localStorage.setItem('treatmentPlanPermission', btoa(hasPermission ? 'true' : 'false'));
  }

  getTreatmentPlanPermission(): boolean {
    const stored = localStorage.getItem('treatmentPlanPermission');
    if (!stored) {
      return false;
    }
    try {
      return atob(stored) === 'true';
    } catch (err) {
      console.warn('Erro ao decodificar treatmentPlanPermission do localStorage.', err);
      return false;
    }
  }

  setCalculatorPermission(hasPermission: boolean): void {
    localStorage.setItem('calculatorPermission', btoa(hasPermission ? 'true' : 'false'));
  }

  getCalculatorPermission(): boolean {
    const stored = localStorage.getItem('calculatorPermission');
    if (!stored) {
      return false;
    }
    try {
      return atob(stored) === 'true';
    } catch (err) {
      console.warn('Erro ao decodificar calculatorPermission do localStorage.', err);
      return false;
    }
  }

  setModules(modules: IModule[]): void {
    localStorage.setItem('modules', btoa(JSON.stringify(modules)));
    
    // Verificar se já existe um módulo selecionado salvo no localStorage
    const storedModule = this.getSelectedModuleFromStorage();
    
    // Se existe um módulo salvo e ele está na lista de módulos disponíveis, mantém a seleção
    if (storedModule && storedModule.id && modules.some(m => m.id === storedModule.id)) {
      this.selectedModuleSubject.next(storedModule);
    } else {
      // Caso contrário, seleciona o primeiro módulo
      const first = modules && modules.length ? modules[0] : { id: '', description: '' } as IModule;
      this.selectedModuleSubject.next(first);
    }
  }

  setSelectedModule(module: IModule): void {
    localStorage.setItem('selectedModule', btoa(JSON.stringify(module)));
    // EMITIR o novo valor para todos os inscritos
    this.selectedModuleSubject.next(module);
  }

  setPatientModelPermission(hasPermission: boolean): void {
    localStorage.setItem('patientModelPermission', btoa(hasPermission ? 'true' : 'false'));
  }

  getPatientModelPermission(): boolean {
    return atob(localStorage.getItem('patientModelPermission') || 'false') === 'true';
  }

  // Método síncrono para obter o valor atual do Subject (para compatibilidade)
  public getSelectedModuleSync(): IModule {
    return this.selectedModuleSubject.getValue();
  }

  setClinicId(clinicId: string): void {
    localStorage.setItem('clinicId', btoa(clinicId));
  }

  getSendDocsType() {
    return atob(localStorage.getItem('sendDocsType') || 'DELIVERY_METHOD_SMS')
  }

  getClinicCnpj(): string {
    return atob(localStorage.getItem('clinicCnpj') || '')
  }

  getClinicId(): string {
    return atob(localStorage.getItem('clinicId') || '')
  }

  getUserId() {
    return atob(localStorage.getItem('userId') || '')
  }

  getToken() {
    // Primeiro tenta obter o token normal
    let token = localStorage.getItem('token');

    // Se não encontrar, tenta obter o token de autenticação de upload
    if (!token) {
      token = localStorage.getItem('auth_token');
      // Se encontrou auth_token, retorna diretamente (sem decodificar)
      if (token) {
        return token;
      }
    }

    // Se encontrou o token normal, decodifica e retorna
    return token ? atob(token) : '';
  }

  getUsername() {
    return atob(localStorage.getItem('username') || '')
  }

  getUserEmail() {
    return atob(localStorage.getItem('userEmail') || '')
  }

  getCompanyName() {
    return atob(localStorage.getItem('companyName') || '')
  }

  getPermission() {
    return atob(localStorage.getItem('permission') || '')
  }

  getUserImage() {
    return atob(localStorage.getItem('userImage') || '')
  }

  getAnamneseAuthType() {
    return atob(localStorage.getItem('anamneseAuthType') || 'NOT_CONTRACTED')
  }

  getHeader() {
    return {
      Authorization: `Bearer ${this.getToken()}`
    }
  }

  getModules() {
    const storedModules = localStorage.getItem('modules');

    // 1. Verifica se existe algum valor no localStorage
    if (storedModules) {
      try {
        // 2. Se existir, decodifica de Base64 e depois faz o parse do JSON
        return JSON.parse(atob(storedModules));
      } catch (e) {
        // 3. Adiciona um tratamento de erro caso o dado esteja corrompido
        console.error("Erro ao decodificar 'modules' do localStorage. Pode estar corrompido.", e);
        return []; // Retorna um valor seguro
      }
    } else {
      // 4. Se não existir, simplesmente retorna o valor padrão (um array vazio)
      return [];
    }
  }

  getSelectedModule() {
    return this.selectedModuleSubject.value; // Retorna o valor atual do BehaviorSubject
  }

  getSelectedModuleFromStorage(): IModule {
    const storedModule = localStorage.getItem('selectedModule');
    if (storedModule) {
      try {
        return JSON.parse(atob(storedModule));
      } catch (e) {
        console.warn("Erro ao decodificar 'selectedModule' do localStorage. Pode estar corrompido.", e);
      }
    }

    // Se não há módulo selecionado, pega o primeiro da lista
    const modules = this.getModules();
    const firstModule = modules[0] || { id: '', description: '' };
  // Não chamar setSelectedModule aqui para evitar acesso ao Subject antes da inicialização
    return firstModule;
  }

  clear() {
    localStorage.removeItem('token')
    localStorage.removeItem('auth_token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('username')
    localStorage.removeItem('userEmail')
    localStorage.removeItem('permission')
    localStorage.removeItem('clinicCnpj')
    localStorage.removeItem('modules')
    localStorage.removeItem('clinicId')
    localStorage.removeItem('selectedModule')
    localStorage.removeItem('treatmentPlanPermission')
    localStorage.removeItem('calculatorPermission')
    localStorage.removeItem('userImage')
    localStorage.removeItem('intercomJwt')
    this.contextService.clearContext();
    this.selectedModuleSubject.next({ id: '', description: '' }); // Limpar o BehaviorSubject
  }

  isLogged() {
    // Verificar token e atualizar o estado de autenticação se necessário
    const token = this.getToken();
    let isLoggedIn = !!token;

    // Verificar se o token JWT está expirado
    if (isLoggedIn && this.isTokenExpired(token)) {
      isLoggedIn = false;
    }

    // Atualizar o estado se necessário
    if (this.authenticated !== isLoggedIn) {
      this.authenticated = isLoggedIn;
    }

    return isLoggedIn;
  }

  /**
   * Verifica se um token JWT está expirado.
   * Retorna true se expirado ou se não for possível decodificar.
   */
  private isTokenExpired(token: string): boolean {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return false; // Não é JWT, não consegue verificar
      }
      const payload = JSON.parse(atob(parts[1]));
      if (!payload.exp) {
        return false; // Sem campo exp, assume válido
      }
      // exp é em segundos, Date.now() em milissegundos
      return payload.exp * 1000 < Date.now();
    } catch {
      return false; // Se não conseguir decodificar, deixa o backend validar
    }
  }

  // Método para definir estado de autenticação
  setAuthenticated(state: boolean): void {
    this.authenticated = state;
  }

  // Verificar se está autenticado
  isAuthenticated(): boolean {
    return this.authenticated;
  }

  // Método para verificar estado da autenticação (útil para debug)
  checkAuthState() {
    const normalToken = localStorage.getItem('token');
    const uploadToken = localStorage.getItem('auth_token');


    if (normalToken) {
      try {
        const decodedToken = atob(normalToken);
      } catch (e) {
      }
    }

    return {
      hasNormalToken: !!normalToken,
      hasUploadToken: !!uploadToken,
      isAuthenticated: this.authenticated
    };
  }
}